import { useEffect } from "react";

import { CLIENT_ID, EVENTS_URL } from "./api.js";

/**
 * Live bijwerken van de app.
 *
 * Het probleem dat dit oplost: een webmail die op een telefoon als app op het
 * beginscherm staat, wordt niet opnieuw geladen als je hem opent — iOS haalt
 * hem gewoon uit de achtergrond terug, met de mailbox van uren geleden. Nieuwe
 * post verscheen pas na een minuut, of pas als je de app helemaal afsloot.
 *
 * Drie bronnen, allemaal naar één kanaal:
 *  - de live-verbinding met de server (Server-Sent Events): "er is post";
 *  - terugkeer in beeld, netwerk terug, of herverbinden: "haal alles opnieuw";
 *  - de service worker, die een pushmelding doorgeeft aan open vensters (het
 *    vangnet als de live-verbinding even weg is).
 *
 * Er komt nooit inhoud over dit kanaal; alleen dát er iets veranderde. De app
 * haalt de details over de gewone API, met de gewone sessiecontrole.
 */

export type LiveMessage =
  /** Nieuwe post afgeleverd in een mailbox van deze browser. */
  | { type: "mail"; account: string }
  /** Iets gewijzigd via de webmail (ander tabblad, ander apparaat). */
  | { type: "changed"; account: string }
  /** Mogelijk iets gemist (achtergrond, offline, verbinding weg): alles verversen. */
  | { type: "resume" }
  /** Een tik op een melding: open deze mailbox. */
  | { type: "open-account"; account: string };

type Handler = (message: LiveMessage) => void;

const handlers = new Set<Handler>();

export function onLive(handler: Handler): () => void {
  handlers.add(handler);
  return () => {
    handlers.delete(handler);
  };
}

function emit(message: LiveMessage): void {
  for (const handler of [...handlers]) handler(message);
}

/**
 * Hoe lang we zonder enig teken van leven (ook geen ping, die de server elke
 * 25 seconden stuurt) blijven geloven dat de verbinding er nog is. Een
 * telefoon die sliep laat soms een socket achter die open lijkt maar dood is.
 */
const WATCHDOG_MS = 70_000;

/** Wachttijden tussen herverbindpogingen na een harde fout (401, 5xx, …). */
const BACKOFF_MS = [1_000, 3_000, 10_000, 30_000];

function parse(data: unknown): Record<string, unknown> {
  if (typeof data !== "string") return {};
  try {
    const value: unknown = JSON.parse(data);
    return typeof value === "object" && value !== null ? (value as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

/**
 * Houdt de live-verbinding open zolang de app in beeld is. `key` verandert als
 * de set aangemelde mailboxen verandert; de server legt die vast bij het
 * openen van de stroom, dus dan verbinden we opnieuw.
 */
export function useLiveConnection(enabled: boolean, key: string): void {
  useEffect(() => {
    if (!enabled) return;
    if (typeof EventSource === "undefined") return;

    let source: EventSource | null = null;
    let retryTimer: number | undefined;
    let watchdog: number | undefined;
    let attempt = 0;

    const clearTimers = (): void => {
      window.clearTimeout(retryTimer);
      window.clearTimeout(watchdog);
      retryTimer = undefined;
      watchdog = undefined;
    };

    const close = (): void => {
      clearTimers();
      source?.close();
      source = null;
    };

    const arm = (): void => {
      window.clearTimeout(watchdog);
      watchdog = window.setTimeout(() => {
        // Te lang stil: opnieuw verbinden en voor de zekerheid alles verversen.
        emit({ type: "resume" });
        open();
      }, WATCHDOG_MS);
    };

    const scheduleRetry = (): void => {
      close();
      const delay = BACKOFF_MS[Math.min(attempt, BACKOFF_MS.length - 1)] ?? 30_000;
      attempt += 1;
      retryTimer = window.setTimeout(() => {
        // Eerst gewoon verversen: is de sessie weg, dan levert dat een 401 op
        // en brengt de app de gebruiker netjes naar het inlogscherm.
        emit({ type: "resume" });
        open();
      }, delay);
    };

    function open(): void {
      close();
      // Een verborgen tabblad hoeft geen verbinding open te houden; bij
      // HTTP/1.1 kost elke open stroom een van de zes verbindingen per site.
      if (document.hidden) return;

      const es = new EventSource(EVENTS_URL);
      source = es;
      // Ook een verbinding die nooit antwoordt moet na een tijdje opnieuw.
      arm();

      let seenReady = false;
      es.addEventListener("ready", () => {
        // Een tweede "ready" op dezelfde EventSource betekent dat de browser
        // zelf herverbonden heeft; wat er tussendoor gebeurde hebben we gemist.
        if (seenReady) emit({ type: "resume" });
        seenReady = true;
        attempt = 0;
        arm();
      });
      es.addEventListener("ping", arm);
      es.addEventListener("mail", (event) => {
        arm();
        const data = parse((event as MessageEvent).data);
        if (typeof data["account"] === "string") emit({ type: "mail", account: data["account"] });
      });
      es.addEventListener("changed", (event) => {
        arm();
        const data = parse((event as MessageEvent).data);
        // Onze eigen wijziging hebben we al op het scherm gezet.
        if (data["origin"] === CLIENT_ID) return;
        if (typeof data["account"] === "string") emit({ type: "changed", account: data["account"] });
      });
      es.onerror = () => {
        // Bij een netwerkfout probeert EventSource het zelf opnieuw (CONNECTING).
        // Bij een foutstatus (401, 409, 5xx) geeft hij het op (CLOSED); dan wij.
        if (es.readyState === EventSource.CLOSED) scheduleRetry();
      };
    }

    /** Terug in beeld: verse verbinding (de oude kan een dode socket zijn) en alles ophalen. */
    const resume = (): void => {
      if (document.hidden) {
        close();
        return;
      }
      attempt = 0;
      emit({ type: "resume" });
      open();
    };

    const onPageShow = (event: PageTransitionEvent): void => {
      // Alleen bij terugkeer uit de bfcache; een gewone eerste lading laadt zelf al.
      if (event.persisted) resume();
    };

    document.addEventListener("visibilitychange", resume);
    window.addEventListener("online", resume);
    window.addEventListener("pageshow", onPageShow);
    open();

    return () => {
      document.removeEventListener("visibilitychange", resume);
      window.removeEventListener("online", resume);
      window.removeEventListener("pageshow", onPageShow);
      close();
    };
  }, [enabled, key]);

  /*
   * De service worker geeft pushmeldingen door aan open vensters, en een tik
   * op een melding wordt hier een "open dit account". Los van de live-
   * verbinding: dit moet ook werken als die net weg is.
   */
  useEffect(() => {
    if (!enabled || !("serviceWorker" in navigator)) return;
    const onMessage = (event: MessageEvent): void => {
      const data: unknown = event.data;
      if (typeof data !== "object" || data === null) return;
      const record = data as Record<string, unknown>;
      const account = typeof record["account"] === "string" ? record["account"] : null;
      if (record["type"] === "new-mail") {
        emit(account === null ? { type: "resume" } : { type: "mail", account });
      } else if (record["type"] === "open-account" && account !== null) {
        emit({ type: "open-account", account });
      }
    };
    navigator.serviceWorker.addEventListener("message", onMessage);
    return () => navigator.serviceWorker.removeEventListener("message", onMessage);
  }, [enabled]);
}
